import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Container,
  Box,
  Alert,
  CircularProgress,
  Snackbar,
  useMediaQuery,
  useTheme,
  IconButton,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Badge,
  Card,
  CardContent,
} from '@mui/material';
import { ArrowBack, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { appStore } from '../stores/AppStore';
import { trackButtonClick, trackNavigation } from '../utils/analytics';
import Header from '../components/Header';
import DeviceList from '../components/DeviceList';
import MessagesList from '../components/MessagesList';
import SendPage from '../components/SendPage';
import ReceivePage from '../components/ReceivePage';
import DevicesPage from '../components/DevicesPage';
import ExternalMessagePage from '../components/ExternalMessagePage';
import ReceiveByCodePage from '../components/ReceiveByCodePage';
import HelpPage from '../components/HelpPage';
import MessageDeletionTimerDialog from '../components/MessageDeletionTimerDialog';
import API_CONFIG from '../config/api.config';

type ViewMode = 'HOME' | 'SEND' | 'RECEIVE' | 'DEVICES' | 'EXTERNAL_SEND' | 'EXTERNAL_RECEIVE' | 'HELP';

const MainPage: React.FC = observer(() => {
  const { logout, handleTokenExpiration } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [viewMode, setViewMode] = useState<ViewMode>('HOME');

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      appStore.showToast('Copied to clipboard');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      appStore.showToast('Failed to copy to clipboard');
    }
  };

  const handleRefreshDevices = () => {
    appStore.loadDevices();
  };

  const handleBackToDevices = () => {
    trackButtonClick('Back', 'Main Page');
    trackNavigation('HOME', 'DEVICES');
    appStore.setSelectedDevice(null);
    setViewMode('HOME');
    // Replace current history state with HOME
    window.history.replaceState({ viewMode: 'HOME' }, '', window.location.href);
  };

  const handleSendClick = () => {
    trackButtonClick('Send', 'Main Page');
    trackNavigation('SEND', 'HOME');
    setViewMode('SEND');
    // Clear device selection to show device list first
    appStore.setSelectedDevice(null);
    // Push state to browser history
    window.history.pushState({ viewMode: 'SEND' }, '', window.location.href);
  };

  const handleReceiveClick = () => {
    trackButtonClick('Receive', 'Main Page');
    trackNavigation('RECEIVE', 'HOME');
    setViewMode('RECEIVE');
    // Show current device messages
    if (appStore.currentDevice) {
      appStore.setSelectedDevice(appStore.currentDevice);
    }
    // Push state to browser history
    window.history.pushState({ viewMode: 'RECEIVE' }, '', window.location.href);
  };

  const handleDevicesClick = () => {
    trackButtonClick('Devices', 'Main Page');
    trackNavigation('DEVICES', 'HOME');
    setViewMode('DEVICES');
    // Push state to browser history
    window.history.pushState({ viewMode: 'DEVICES' }, '', window.location.href);
  };

  const handleHelpClick = () => {
    trackButtonClick('Help', 'Main Page');
    trackNavigation('HELP', 'HOME');
    setViewMode('HELP');
    // Push state to browser history
    window.history.pushState({ viewMode: 'HELP' }, '', window.location.href);
  };

  const handleBackFromHelp = () => {
    trackButtonClick('Back from Help', 'Main Page');
    trackNavigation('HOME', 'HELP');
    setViewMode('HOME');
    // Replace current history state with HOME
    window.history.replaceState({ viewMode: 'HOME' }, '', window.location.href);
  };

  const handleDeviceClickFromDevicesPage = (device: any) => {
    // Set the selected device and navigate to Send page
    appStore.setSelectedDevice(device);
    setViewMode('SEND');
    // Push state to browser history
    window.history.pushState({ viewMode: 'SEND' }, '', window.location.href);
  };

  // Calculate unread count for current device
  // Load messages for current device to get accurate count
  React.useEffect(() => {
    if (appStore.currentDevice && viewMode === 'HOME') {
      // Set selected device to current device to load its messages
      if (!appStore.selectedDevice || appStore.selectedDevice.id !== appStore.currentDevice.id) {
        appStore.setSelectedDevice(appStore.currentDevice);
      }
      appStore.loadMessages(appStore.currentDevice.id, false);
    }
  }, [appStore.currentDevice?.id, viewMode]);

  // Reload messages when returning from RECEIVE view to update read status
  const prevViewMode = React.useRef<ViewMode>(viewMode);
  React.useEffect(() => {
    // If we just returned from RECEIVE view to HOME, reload messages to update read status
    if (prevViewMode.current === 'RECEIVE' && viewMode === 'HOME' && appStore.currentDevice) {
      // Reload messages to get updated read status after viewing them
      // Use a small delay to ensure local storage is updated
      setTimeout(() => {
        appStore.loadMessages(appStore.currentDevice!.id, false);
      }, 100);
    }
    prevViewMode.current = viewMode;
  }, [viewMode, appStore.currentDevice]);

  const unreadCount = React.useMemo(() => {
    if (!appStore.currentDevice) return 0;
    // Count unread messages - messages are already filtered by device when loaded
    const count = appStore.messages.filter(msg => !msg.isRead).length;
    return count;
  }, [appStore.messages, appStore.currentDevice]);

  // Track current viewMode with ref for popstate handler
  const viewModeRef = React.useRef<ViewMode>(viewMode);
  React.useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  // Handle browser back button
  useEffect(() => {
    // Push initial HOME state to history if we're on HOME
    if (viewMode === 'HOME') {
      window.history.replaceState({ viewMode: 'HOME' }, '', window.location.href);
    }

    const handlePopState = (event: PopStateEvent) => {
      // When back button is pressed, navigate back to HOME
      // This handles back button from SEND, RECEIVE, or DEVICES pages
      const currentViewMode = viewModeRef.current;
      if (currentViewMode !== 'HOME') {
        // Navigate back to HOME
        appStore.setSelectedDevice(null);
        setViewMode('HOME');
        // Replace current state to prevent going back further
        window.history.replaceState({ viewMode: 'HOME' }, '', window.location.href);
      }
      // If already on HOME, let browser handle navigation normally (might navigate away from app)
    };

    // Listen for browser back/forward button
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [viewMode]);

  // Initialize app
  useEffect(() => {
    const initializeApp = async () => {
      appStore.setLoading(true);
      await appStore.checkServerConnection();
      await appStore.loadDevices();
      appStore.setLoading(false);
    };

    initializeApp();

    // Set up periodic refresh for messages (using config)
    const messageInterval = setInterval(() => {
      // Always check for new messages on current device (web browser) in background
      if (appStore.currentDevice) {
        appStore.loadMessages(appStore.currentDevice.id, false); // Don't show loading indicator on periodic refresh
      }
    }, API_CONFIG.MESSAGE_REFRESH_INTERVAL);

    // Set up periodic health check (using config)
    const healthInterval = setInterval(() => {
      appStore.checkServerConnection();
    }, API_CONFIG.HEALTH_CHECK_INTERVAL);

    return () => {
      clearInterval(messageInterval);
      clearInterval(healthInterval);
    };
  }, []);

  if (appStore.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        flexGrow: 1, 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        background: 'linear-gradient(to bottom, #1D2B45 0%, #A2B4CB 100%)',
        backgroundAttachment: 'fixed',
      }}
    >
      <Header 
        onLogout={handleLogout}
        onRefreshDevices={handleRefreshDevices}
        onDevicesClick={handleDevicesClick}
        onHelpClick={handleHelpClick}
      />

      {/* My Devices header when on DEVICES view */}
      {viewMode === 'DEVICES' && (
        <AppBar position="static" sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
          <Toolbar variant="dense">
            <IconButton edge="start" onClick={handleBackToDevices} sx={{ mr: 2, color: '#FFFFFF' }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" noWrap sx={{ color: '#FFFFFF', fontWeight: 400, fontSize: '1rem' }}>
              My devices
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Back button when not on HOME view */}
      {viewMode !== 'HOME' && viewMode !== 'DEVICES' && viewMode !== 'EXTERNAL_SEND' && viewMode !== 'EXTERNAL_RECEIVE' && viewMode !== 'HELP' && (
        <AppBar position="static" sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
          <Toolbar variant="dense">
            <IconButton 
              edge="start" 
              onClick={() => {
                trackButtonClick('Back', viewMode);
                handleBackToDevices();
              }} 
              sx={{ mr: 2, color: '#FFFFFF' }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="subtitle1" noWrap sx={{ color: '#FFFFFF' }}>
              {viewMode === 'SEND' 
                ? 'Send files' 
                : viewMode === 'RECEIVE' 
                ? (appStore.selectedDevice?.deviceType === 'web' 
                    ? 'Incoming Messages' 
                    : `Messages to ${appStore.selectedDevice?.name || ''}`)
                : ''}
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      <Container 
        maxWidth="lg" 
        sx={{ 
          mt: { xs: viewMode !== 'HOME' && viewMode !== 'DEVICES' && viewMode !== 'HELP' ? 0 : 1, sm: viewMode !== 'HOME' && viewMode !== 'DEVICES' && viewMode !== 'HELP' ? 0 : 1.5 }, 
          mb: { xs: 0, sm: 1.5 },
          px: { xs: 0.5, sm: 1, md: 1.5 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: viewMode === 'HOME' || viewMode === 'HELP' ? 'auto' : 'hidden', // Allow scrolling on HOME and HELP views
          minHeight: 0, // Allow flex shrinking
        }}
      >
        {appStore.error && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => appStore.clearError()}>
            {appStore.error}
          </Alert>
        )}

        {viewMode === 'HOME' ? (
          /* Home View with Send and Receive buttons */
          <Box 
            sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              py: { xs: 2, sm: 3 },
              px: { xs: 1.5, sm: 3 },
              pb: { xs: 6, sm: 3 }, // Extra bottom padding on mobile to prevent cutoff
              minHeight: 0, // Allow flex shrinking
              overflow: 'auto', // Allow scrolling if needed
            }}
          >
            {/* Title and Description */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography 
                variant="h3" 
                sx={{ 
                  color: '#FFFFFF', 
                  fontWeight: 'bold',
                  mb: 1,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                }}
              >
                BufferioApp
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  mb: 2
                }}
              >
                Share clipboard content across your devices
              </Typography>
            </Box>

            {/* How it works section */}
            <Card 
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.12)', 
                backdropFilter: 'blur(10px)',
                mb: 2,
                maxWidth: { xs: '100%', sm: '600px' },
                width: '100%'
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#FFFFFF', 
                    fontWeight: 'bold',
                    mb: 1.5,
                    fontSize: { xs: '1rem', sm: '1.1rem' }
                  }}
                >
                  How it works:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ArrowUpward sx={{ color: '#FFFFFF', fontSize: 20 }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: { xs: '0.875rem', sm: '0.95rem' }
                      }}
                    >
                      Send files or messages to other devices
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ArrowDownward sx={{ color: '#FFFFFF', fontSize: 20 }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: { xs: '0.875rem', sm: '0.95rem' }
                      }}
                    >
                      Receive content sent to this device
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Send and Receive Buttons */}
            <Box 
              sx={{ 
                display: 'flex', 
                gap: { xs: 2, sm: 3 },
                width: '100%',
                maxWidth: { xs: '100%', sm: '600px' },
                justifyContent: 'center',
                alignItems: 'stretch',
                mt: 'auto', // Push to bottom but allow space
                mb: { xs: 4, sm: 0 }, // Bottom margin on mobile to prevent cutoff
              }}
            >
              {/* Send Button */}
              <Box 
                sx={{ 
                  flex: 1, 
                  maxWidth: { xs: '100%', sm: '220px' },
                  minWidth: 0, // Prevent flex shrinking issues
                  display: 'flex'
                }}
              >
                <Button
                  fullWidth
                  onClick={handleSendClick}
                  sx={{
                    width: '100%',
                    aspectRatio: '1',
                    minHeight: { xs: 140, sm: 180 },
                    maxHeight: { xs: 140, sm: 180 },
                    background: 'linear-gradient(135deg, #FF7E5F 0%, #FEB47B 100%)',
                    borderRadius: '28px',
                    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.3)',
                    color: '#FFFFFF',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    padding: 0,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FF8E6F 0%, #FEC48B 100%)',
                      boxShadow: '0 12px 24px rgba(0, 0, 0, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <ArrowUpward sx={{ fontSize: { xs: 36, sm: 48 } }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                    Send
                  </Typography>
                </Button>
              </Box>

              {/* Receive Button */}
              <Box 
                sx={{ 
                  flex: 1, 
                  maxWidth: { xs: '100%', sm: '220px' },
                  minWidth: 0, // Prevent flex shrinking issues
                  position: 'relative',
                  display: 'flex'
                }}
              >
                <Badge
                  badgeContent={unreadCount > 0 ? (unreadCount > 99 ? '99+' : unreadCount.toString()) : undefined}
                  color="error"
                  showZero={false}
                  sx={{
                    width: '100%',
                    '& .MuiBadge-badge': {
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      minWidth: '24px',
                      height: '24px',
                      top: 8,
                      right: 8,
                      backgroundColor: '#F44336',
                      zIndex: 1,
                    }
                  }}
                  invisible={unreadCount === 0}
                >
                  <Button
                    fullWidth
                    onClick={handleReceiveClick}
                    sx={{
                      width: '100%',
                      aspectRatio: '1',
                      minHeight: { xs: 140, sm: 180 },
                      maxHeight: { xs: 140, sm: 180 },
                      background: 'linear-gradient(135deg, #4FACFE 0%, #00F2FE 100%)',
                      borderRadius: '28px',
                      boxShadow: '0 10px 20px rgba(0, 0, 0, 0.3)',
                      color: '#FFFFFF',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      padding: 0,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5FBCFE 0%, #10F2FE 100%)',
                        boxShadow: '0 12px 24px rgba(0, 0, 0, 0.4)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <ArrowDownward sx={{ fontSize: { xs: 36, sm: 48 } }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                      Receive
                    </Typography>
                  </Button>
                </Badge>
              </Box>
            </Box>
          </Box>
        ) : viewMode === 'SEND' ? (
          /* Send Page */
          <SendPage 
            onBack={handleBackToDevices}
            onExternalMessage={() => {
              trackButtonClick('External Send', 'Send Page');
              trackNavigation('EXTERNAL_SEND', 'SEND');
              setViewMode('EXTERNAL_SEND');
              window.history.pushState({ viewMode: 'EXTERNAL_SEND' }, '', window.location.href);
            }}
          />
        ) : viewMode === 'RECEIVE' ? (
          /* Receive Page */
          <ReceivePage 
            onBack={handleBackToDevices}
            onReceiveByCode={() => {
              trackButtonClick('External Receive', 'Receive Page');
              trackNavigation('EXTERNAL_RECEIVE', 'RECEIVE');
              setViewMode('EXTERNAL_RECEIVE');
              window.history.pushState({ viewMode: 'EXTERNAL_RECEIVE' }, '', window.location.href);
            }}
          />
        ) : viewMode === 'DEVICES' ? (
          /* Devices Page */
          <DevicesPage onBack={handleBackToDevices} onDeviceClick={handleDeviceClickFromDevicesPage} onLogout={handleLogout} />
        ) : viewMode === 'EXTERNAL_SEND' ? (
          /* External Message Send Page */
          <ExternalMessagePage onBack={() => {
            trackButtonClick('Back from External Send', 'Main Page');
            trackNavigation('SEND', 'EXTERNAL_SEND');
            setViewMode('SEND');
            window.history.pushState({ viewMode: 'SEND' }, '', window.location.href);
          }} />
        ) : viewMode === 'EXTERNAL_RECEIVE' ? (
          /* Receive by Code Page */
          <ReceiveByCodePage onBack={() => {
            trackButtonClick('Back from External Receive', 'Main Page');
            trackNavigation('RECEIVE', 'EXTERNAL_RECEIVE');
            setViewMode('RECEIVE');
            window.history.pushState({ viewMode: 'RECEIVE' }, '', window.location.href);
          }} />
        ) : viewMode === 'HELP' ? (
          /* Help Page */
          <HelpPage onBack={handleBackFromHelp} />
        ) : (
          /* Desktop: Side-by-side layout (fallback) */
          !isMobile ? (
            <Box sx={{ display: 'flex', gap: 1.5, height: '100%' }}>
              {/* Devices Section */}
              <Box sx={{ flex: '0 0 300px' }}>
                <DeviceList onRefresh={handleRefreshDevices} onLogout={handleLogout} />
              </Box>

              {/* Messages Section */}
              <Box sx={{ flex: 1 }}>
                <MessagesList onCopyToClipboard={copyToClipboard} />
              </Box>
            </Box>
          ) : (
            /* Mobile: Show devices or messages based on selection */
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {!appStore.selectedDevice ? (
                <DeviceList onRefresh={handleRefreshDevices} onLogout={handleLogout} />
              ) : (
                <MessagesList onCopyToClipboard={copyToClipboard} />
              )}
            </Box>
          )
        )}
      </Container>

      {/* Toast Notification */}
      <Snackbar
        open={appStore.toast.show}
        autoHideDuration={3000}
        onClose={() => appStore.hideToast()}
        message={appStore.toast.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Message Deletion Timer Dialog */}
      <MessageDeletionTimerDialog
        open={appStore.showDeletionTimerDialog}
        onClose={() => appStore.hideDeletionTimer()}
      />
    </Box>
  );
});

export default MainPage;